import * as XLSX from 'xlsx';
import { downloadFile, generateFileName } from '../../utils/fileUtils';
import { processTimeTableForExcel, getColumnInfo } from './timeTableTeacherExport.js';

class TimeTableExcelService {
  /**
   * Helper function to get teacher abbreviation
   * @param {string} teacherName - Full teacher name
   * @returns {string} Abbreviated teacher name
   */
  static getTeacherAbbreviation(teacherName) {
    if (!teacherName) return '';
    
    const words = teacherName.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase();
    }
    
    return words.map(word => word.charAt(0)).join('').toUpperCase();
  }

  /**
   * Helper function to abbreviate subject names
   * @param {string} subjectName - Full subject name
   * @returns {string} Abbreviated subject name
   */
  static getSubjectAbbreviation(subjectName) {
    if (!subjectName) return '';
    
    // Common subject abbreviations
    const subjectMap = {
      'mathematics': 'Math',
      'english': 'Eng',
      'science': 'Sci',
      'social studies': 'SS',
      'physical education': 'PE',
      'computer science': 'CS',
      'information technology': 'IT',
      'physics': 'Phy',
      'chemistry': 'Chem',
      'biology': 'Bio',
      'history': 'Hist',
      'geography': 'Geo',
      'economics': 'Econ',
      'political science': 'PolSci',
      'psychology': 'Psych',
      'philosophy': 'Phil',
      'literature': 'Lit',
      'art': 'Art',
      'music': 'Music',
      'business studies': 'Bus',
      'accounting': 'Acc',
      'statistics': 'Stats',
      'calculus': 'Calc',
      'geometry': 'Geom',
      'environmental studies': 'EVS',
      'health': 'Health',
      'foreign language': 'FL',
      'spanish': 'Span',
      'french': 'Fr',
      'german': 'Ger',
      'japanese': 'Jpn',
      'arabic': 'Arab',
      'hindi': 'Hindi',
      'tamil': 'Tamil',
      'malayalam': 'Mal',
      'telugu': 'Tel',
      'kannada': 'Kan',
      'urdu': 'Urdu',
      'sanskrit': 'Sans',
      'communicative english': 'CE',
      'fiqh &thareekh': 'Fiq&Th',
      'fiqh & thareekh': 'Fiq&Th',
      'qur\'an':'Qur',
      'quran': 'Qur'
    };

    // Check if there's a predefined abbreviation
    if (subjectMap[subjectName.toLowerCase()]) {
      return subjectMap[subjectName.toLowerCase()];
    }

    // If not found in map, create abbreviation from words
    const words = subjectName.trim().split(' ');
    if (words.length === 1) {
      // Single word: take first 4 characters
      return words[0].substring(0, 4);
    } else if (words.length === 2) {
      // Two words: take first 2-3 chars from each
      return words[0].substring(0, 2) + words[1].substring(0, 2);
    } else {
      // Multiple words: take first letter of each
      return words.map(word => word.charAt(0)).join('').toUpperCase();
    }
  }

  /**
   * Process time table data to create grid structure
   * @param {Array} timeTableData - Array of TimeTablePeriod objects
   * @returns {Object} Processed grid data
   */
  static processTimeTableData(timeTableData) {
    if (!timeTableData || timeTableData.length === 0) {
      return { classRows: [], teacherColumns: [], gridData: {} };
    }

    // Get unique class-section combinations
    const classSet = new Set();
    const teacherSet = new Set();

    timeTableData.forEach(period => {
      if (period.isClassPeriod()) {
        const classIdentifier = period.getClassIdentifier();
        classSet.add(classIdentifier);
        
        if (period.teacherName) {
          teacherSet.add(period.teacherName);
        }
      }
    });

    // Custom sorting for classes: class number first, then section
    const classRows = Array.from(classSet).sort((a, b) => {
      const [classA, sectionA] = a.split('-');
      const [classB, sectionB] = b.split('-');
      
      const classNumA = parseInt(classA) || 0;
      const classNumB = parseInt(classB) || 0;
      
      if (classNumA !== classNumB) {
        return classNumA - classNumB;
      }
      
      return (sectionA || '').localeCompare(sectionB || '');
    });
    
    const teacherColumns = Array.from(teacherSet).sort();

    // Create grid data mapping
    const gridData = {};
    timeTableData.forEach(period => {
      if (period.isClassPeriod()) {
        const classIdentifier = period.getClassIdentifier();
        const teacherName = period.teacherName;
        
        if (!gridData[classIdentifier]) {
          gridData[classIdentifier] = {};
        }
        
        if (teacherName) {
          if (!gridData[classIdentifier][teacherName]) {
            gridData[classIdentifier][teacherName] = [];
          }
          gridData[classIdentifier][teacherName].push(period);
        }
      }
    });

    return { classRows, teacherColumns, gridData };
  }

  /**
   * Get cell content for a class-teacher combination
   * @param {string} classIdentifier - Class identifier (e.g., "10-A")
   * @param {string} teacherName - Teacher name
   * @param {Object} gridData - Grid data mapping
   * @returns {string} Cell content
   */
  static getCellContent(classIdentifier, teacherName, gridData) {
    const periods = gridData[classIdentifier]?.[teacherName] || [];
    if (periods.length === 0) return '';

    // Count occurrences of each subject
    const subjectCounts = {};
    periods.forEach(period => {
      const subject = period.subject;
      if (subject) {
        const abbreviatedSubject = this.getSubjectAbbreviation(subject);
        subjectCounts[abbreviatedSubject] = (subjectCounts[abbreviatedSubject] || 0) + 1;
      }
    });

    // Format as "Subject (count)" and join with commas
    return Object.entries(subjectCounts)
      .map(([subject, count]) => count > 1 ? `${subject}(${count})` : subject)
      .join(', ');
  }

  /**
   * Create Excel worksheet with compact formatting for printing
   * @param {Array} data - 2D array of data
   * @param {string} title - Title for the worksheet
   * @returns {Object} Excel worksheet
   */
  static createCompactWorksheet(data, title) {
    // Add title row
    const worksheetData = [
      [title], // Title row
      [], // Empty row for spacing
      ...data
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set page setup for landscape printing
    worksheet['!pageSetup'] = {
      orientation: 'landscape',
      paperSize: 9, // A4
      fitToWidth: 1,
      fitToHeight: 0, // Allow multiple pages vertically if needed
      scale: 85 // Reduce scale to fit more content
    };

    // Set print margins (in inches)
    worksheet['!margins'] = {
      left: 0.5,
      right: 0.5,
      top: 0.75,
      bottom: 0.75,
      header: 0.3,
      footer: 0.3
    };

    // Calculate compact column widths
    const columnWidths = this.calculateCompactColumnWidths(data);
    worksheet['!cols'] = columnWidths;

    // Set row heights for compact display with text wrapping
    worksheet['!rows'] = [];
    worksheetData.forEach((row, index) => {
      if (index === 0) {
        // Title row - larger height
        worksheet['!rows'][index] = { hpt: 25 };
      } else if (index === 1) {
        // Empty row - smaller height
        worksheet['!rows'][index] = { hpt: 8 };
      } else if (index === 2) {
        // Header row - medium height for wrapped text
        worksheet['!rows'][index] = { hpt: 20 };
      } else {
        // Data rows - height to accommodate wrapped text
        worksheet['!rows'][index] = { hpt: 18 };
      }
    });

    // Style the title cell
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    if (data.length > 0) {
      worksheet['!merges'].push({
        s: { r: 0, c: 0 },
        e: { r: 0, c: data[0].length - 1 }
      });
    }

    // Apply cell styles
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};

        if (R === 0) {
          // Title row styling
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 12 },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
          };
        } else if (R === 2) {
          // Header row styling
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 9 },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            fill: { fgColor: { rgb: 'E6E6E6' } },
            border: {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' }
            }
          };
        } else if (R > 2) {
          // Data cell styling
          worksheet[cellAddress].s = {
            font: { sz: 8 }, // Smaller font for compact display
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' }
            }
          };

          // Special styling for class column (first column)
          if (C === 0) {
            worksheet[cellAddress].s.font.bold = true;
            worksheet[cellAddress].s.fill = { fgColor: { rgb: 'F0F0F0' } };
          }
        }
      }
    }

    return worksheet;
  }

  /**
   * Calculate compact column widths for printing
   * @param {Array} data - 2D array of data
   * @returns {Array} Array of column width specifications
   */
  static calculateCompactColumnWidths(data) {
    const columnWidths = [];
    
    data.forEach(row => {
      row.forEach((cell, colIndex) => {
        const cellValue = String(cell || '');
        const currentWidth = columnWidths[colIndex]?.wch || 0;
        
        // Calculate width based on content, but keep it compact
        let newWidth;
        if (colIndex === 0) {
          // Class column - slightly wider
          newWidth = Math.max(cellValue.length, 6);
        } else {
          // Teacher columns - compact width
          newWidth = Math.max(cellValue.length, 4);
        }
        
        if (!columnWidths[colIndex]) {
          columnWidths[colIndex] = { wch: newWidth };
        } else if (newWidth > currentWidth) {
          columnWidths[colIndex].wch = newWidth;
        }
      });
    });

    // Ensure compact widths for printing
    return columnWidths.map((col, index) => ({
      wch: Math.min(Math.max(col.wch, index === 0 ? 6 : 4), index === 0 ? 12 : 10)
    }));
  }

  /**
   * Create teacher annexure data
   * @param {Array} teacherColumns - Array of teacher names
   * @returns {Array} 2D array for teacher annexure
   */
  static createTeacherAnnexure(teacherColumns) {
    const annexureData = [
      ['Teacher Abbreviations', ''], // Title with empty second column
      ['', ''], // Empty row
      ['Abbreviation', 'Full Name'], // Headers
    ];

    // Add teacher data
    teacherColumns.forEach(teacher => {
      const abbreviation = this.getTeacherAbbreviation(teacher);
      annexureData.push([abbreviation, teacher]);
    });

    console.log('Final annexure data structure:', annexureData);
    return annexureData;
  }

  /**
   * Create teacher annexure worksheet
   * @param {Array} teacherColumns - Array of teacher names
   * @returns {Object} Excel worksheet for teacher annexure
   */
  static createTeacherAnnexureWorksheet(teacherColumns) {
    const annexureData = this.createTeacherAnnexure(teacherColumns);
    console.log('Annexure data:', annexureData);
    
    const worksheet = XLSX.utils.aoa_to_sheet(annexureData);

    // Set column widths
    worksheet['!cols'] = [
      { wch: 15 }, // Abbreviation column
      { wch: 35 }  // Full name column
    ];

    // Merge title cell
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    worksheet['!merges'].push({
      s: { r: 0, c: 0 },
      e: { r: 0, c: 1 }
    });

    // Apply basic styles
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};

        if (R === 0) {
          // Title row
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: 'center', vertical: 'center' }
          };
        } else if (R === 2) {
          // Header row
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 11 },
            alignment: { horizontal: 'center', vertical: 'center' }
          };
        } else if (R > 2) {
          // Data rows
          worksheet[cellAddress].s = {
            font: { sz: 10 },
            alignment: { 
              horizontal: C === 0 ? 'center' : 'left', 
              vertical: 'center' 
            }
          };
        }
      }
    }

    return worksheet;
  }

  /**
   * Export time table teacher allotment grid to Excel
   * @param {Array} timeTableData - Array of TimeTablePeriod objects
   * @param {string} schoolId - School identifier
   * @param {function} onSuccess - Success callback
   * @param {function} onError - Error callback
   */
  static exportTeacherAllotmentGrid(timeTableData, schoolId, onSuccess, onError) {
    try {
      const { classRows, teacherColumns, gridData } = this.processTimeTableData(timeTableData);

      if (classRows.length === 0) {
        throw new Error('No class periods found in the time table data');
      }

      // Create header row with abbreviated teacher names
      const headerRow = ['Class', ...teacherColumns.map(teacher => this.getTeacherAbbreviation(teacher))];

      // Create data rows
      const dataRows = classRows.map(classIdentifier => {
        const row = [classIdentifier];
        teacherColumns.forEach(teacher => {
          const cellContent = this.getCellContent(classIdentifier, teacher, gridData);
          row.push(cellContent || '');
        });
        return row;
      });

      // Combine header and data
      const excelData = [headerRow, ...dataRows];

      // Create main worksheet
      const title = `Teacher Allotment - School ${schoolId}`;
      const mainWorksheet = this.createCompactWorksheet(excelData, title);

      // Create teacher annexure worksheet
      console.log('Creating annexure for teachers:', teacherColumns);
      const annexureWorksheet = this.createTeacherAnnexureWorksheet(teacherColumns);
      console.log('Annexure worksheet created:', annexureWorksheet);

      // Create workbook with both sheets
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Teacher Allotment');
      XLSX.utils.book_append_sheet(workbook, annexureWorksheet, 'Teacher Names');
      console.log('Workbook sheets:', workbook.SheetNames);

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        type: 'buffer',
        bookType: 'xlsx',
        cellStyles: true
      });

      // Convert buffer to blob
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Download file
      const fileName = generateFileName(`teacher_allotment_${schoolId}`, 'xlsx');
      downloadFile(blob, fileName);

      if (onSuccess) {
        onSuccess(fileName);
      }

    } catch (error) {
      console.error('Error exporting teacher allotment grid:', error);
      if (onError) {
        onError(error.message || 'Failed to export teacher allotment grid');
      }
    }
  }

  /**
   * Create Excel worksheet for timetable with day/period structure
   * @param {Array} data - 2D array of timetable data
   * @param {Object} columnInfo - Column information with day spans and gaps
   * @param {string} title - Title for the worksheet
   * @returns {Object} Excel worksheet
   */
  static createTimeTableWorksheet(data, columnInfo, title) {
    // Add title row
    const worksheetData = [
      [title], // Title row
      [], // Empty row for spacing
      ...data
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set page setup for landscape printing
    worksheet['!pageSetup'] = {
      orientation: 'landscape',
      paperSize: 9, // A4
      fitToWidth: 1,
      fitToHeight: 0, // Allow multiple pages vertically if needed
      scale: 75 // Reduce scale to fit more content
    };

    // Set print margins (in inches)
    worksheet['!margins'] = {
      left: 0.4,
      right: 0.4,
      top: 0.75,
      bottom: 0.75,
      header: 0.3,
      footer: 0.3
    };

    // Calculate column widths for timetable
    const columnWidths = this.calculateTimeTableColumnWidths(data, columnInfo);
    worksheet['!cols'] = columnWidths;

    // Set row heights
    worksheet['!rows'] = [];
    worksheetData.forEach((row, index) => {
      if (index === 0) {
        // Title row - larger height
        worksheet['!rows'][index] = { hpt: 25 };
      } else if (index === 1) {
        // Empty row - smaller height
        worksheet['!rows'][index] = { hpt: 8 };
      } else if (index === 2 || index === 3) {
        // Header rows - medium height
        worksheet['!rows'][index] = { hpt: 20 };
      } else {
        // Data rows - height to accommodate content
        worksheet['!rows'][index] = { hpt: 18 };
      }
    });

    // Merge title cell across all columns
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    if (data.length > 0) {
      worksheet['!merges'].push({
        s: { r: 0, c: 0 },
        e: { r: 0, c: data[0].length - 1 }
      });
    }

    // Merge day headers across their periods
    columnInfo.daySpans.forEach(daySpan => {
      if (daySpan.periodCount > 1) {
        worksheet['!merges'].push({
          s: { r: 2, c: daySpan.startColumn },
          e: { r: 2, c: daySpan.endColumn }
        });
      }
    });

    // Apply cell styles
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) continue;

        if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};

        if (R === 0) {
          // Title row styling
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 14 },
            alignment: { horizontal: 'center', vertical: 'center' }
          };
        } else if (R === 2 || R === 3) {
          // Header rows styling
          const isGapColumn = columnInfo.gapColumns.includes(C);
          if (!isGapColumn) {
            worksheet[cellAddress].s = {
              font: { bold: true, sz: 10 },
              alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
              fill: { fgColor: { rgb: 'E6E6E6' } },
              border: {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
              }
            };
          }
                  } else if (R > 3) {
          // Data cell styling
          const isGapColumn = columnInfo.gapColumns.includes(C);
          if (!isGapColumn) {
            worksheet[cellAddress].s = {
              font: { sz: 9 },
              alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
              border: {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
              }
            };

            // Special styling for teacher names column (first column)
            if (C === 0) {
              worksheet[cellAddress].s.font.bold = true;
              worksheet[cellAddress].s.fill = { fgColor: { rgb: 'F0F0F0' } };
            }
          }
        }
      }
    }

    return worksheet;
  }

  /**
   * Calculate column widths for timetable format (Teacher-centric view)
   * @param {Array} data - 2D array of data
   * @param {Object} columnInfo - Column information with day spans and gaps
   * @returns {Array} Array of column width specifications
   */
  static calculateTimeTableColumnWidths(data, columnInfo) {
    const columnWidths = [];
    
    data.forEach(row => {
      row.forEach((cell, colIndex) => {
        const cellValue = String(cell || '');
        const currentWidth = columnWidths[colIndex]?.wch || 0;
        
        // Calculate width based on content and column type
        let newWidth;
        const isGapColumn = columnInfo.gapColumns.includes(colIndex);
        
        if (colIndex === 0) {
          // Teacher Names column - for first name + abbreviation format
          newWidth = Math.max(cellValue.length, 8);
        } else if (isGapColumn) {
          // Gap columns - minimal width
          newWidth = 2;
        } else {
          // Period columns - width for subject abbreviations and class identifiers
          newWidth = Math.max(cellValue.length, 8);
        }
        
        if (!columnWidths[colIndex]) {
          columnWidths[colIndex] = { wch: newWidth };
        } else if (newWidth > currentWidth) {
          columnWidths[colIndex].wch = newWidth;
        }
      });
    });

    // Ensure appropriate widths
    return columnWidths.map((col, index) => {
      const isGapColumn = columnInfo.gapColumns.includes(index);
      if (index === 0) {
        // Teacher Names column - width for first name + abbreviation format
        return { wch: Math.min(Math.max(col.wch, 8), 15) };
      } else if (isGapColumn) {
        // Gap columns
        return { wch: 2 };
      } else {
        // Period columns - wider for subject(class) format
        return { wch: Math.min(Math.max(col.wch, 8), 14) };
      }
    });
  }

  /**
   * Export timetable in day/period format to Excel
   * @param {Array} timeTableData - Array of TimeTablePeriod objects
   * @param {string} schoolId - School identifier
   * @param {function} onSuccess - Success callback
   * @param {function} onError - Error callback
   */
  static exportTimeTableByDayPeriod(timeTableData, schoolId, onSuccess, onError) {
    try {
      // Process timetable data using the new format
      const excelData = processTimeTableForExcel(timeTableData);
      const columnInfo = getColumnInfo(timeTableData);

      if (excelData.length <= 2) {
        throw new Error('No timetable data available for export');
      }

      // Create main worksheet
      const title = `Teacher Timetable - School ${schoolId}`;
      const mainWorksheet = this.createTimeTableWorksheet(excelData, columnInfo, title);

      // Create teacher allotment worksheet
      const { classRows, teacherColumns, gridData } = this.processTimeTableData(timeTableData);
      
      // Create teacher allotment data
      const teacherHeaderRow = ['Class', ...teacherColumns.map(teacher => this.getTeacherAbbreviation(teacher))];
      const teacherDataRows = classRows.map(classIdentifier => {
        const row = [classIdentifier];
        teacherColumns.forEach(teacher => {
          const cellContent = this.getCellContent(classIdentifier, teacher, gridData);
          row.push(cellContent || '');
        });
        return row;
      });
      const teacherExcelData = [teacherHeaderRow, ...teacherDataRows];
      
      // Create teacher allotment worksheet
      const teacherTitle = `Teacher Allotment - School ${schoolId}`;
      const teacherWorksheet = this.createCompactWorksheet(teacherExcelData, teacherTitle);

      // Create teacher annexure worksheet
      const annexureWorksheet = this.createTeacherAnnexureWorksheet(teacherColumns);

      // Create workbook with all sheets
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Teacher Timetable');
      XLSX.utils.book_append_sheet(workbook, teacherWorksheet, 'Teacher Allotment');
      XLSX.utils.book_append_sheet(workbook, annexureWorksheet, 'Teacher Names');

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, {
        type: 'buffer',
        bookType: 'xlsx',
        cellStyles: true
      });

      // Convert buffer to blob
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Download file
      const fileName = generateFileName(`class_timetable_${schoolId}`, 'xlsx');
      downloadFile(blob, fileName);

      if (onSuccess) {
        onSuccess(fileName);
      }

    } catch (error) {
      console.error('Error exporting class timetable:', error);
      if (onError) {
        onError(error.message || 'Failed to export class timetable');
      }
    }
  }
}

export default TimeTableExcelService; 